import React, { useState } from 'react';
import { Box, Container, Typography, Divider, ToggleButtonGroup, ToggleButton, Button, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CategoryIcon from '@mui/icons-material/Category';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { IconButton } from '@mui/material';
import AddToCartControl from '../UserDashboard/AddToCartControl';
import ClientOrderRow from '../AdminDashboard/ClientOrderRow';
import OrderDetailDialog from './OrderDetailDialog';

import PageHeader from './PageHeader';
import MetricCard from './MetricCard';
import StatusChip from './StatusChip';
import CategoryPill from './CategoryPill';
import DataTableCard from './DataTableCard';
import FilterChips from './FilterChips';
import BulkBar from './BulkBar';
import FieldRow from './FieldRow';
import EntityDrawer from './EntityDrawer';
import ConfirmDialog from './ConfirmDialog';
import SkeletonCards from './SkeletonCards';
import StockLevel from './StockLevel';
import QuantityStepper from './QuantityStepper';
import RowThumb from './RowThumb';
import ProductPicker from './ProductPicker';
import WizardDrawer from './WizardDrawer';
import { TextField } from '@mui/material';

const MOCK_PRODUCTS = [
  { id: 'p1', name: 'Stretch Wrap 18" x 1500ft', description: 'Industrial cast stretch film, 80 gauge', price: '$42.50', unit: 'case', image: null },
  { id: 'p2', name: 'Nitrile Gloves — Box of 100', description: 'Powder-free, 4 mil, blue', price: '$11.90', unit: 'box', image: null },
  { id: 'p3', name: 'Packing Tape 48mm', description: 'Acrylic adhesive, clear', price: '$68.00', unit: 'case', image: null },
];

const MOCK_ROWS = [
  { id: 'p1', name: 'Stretch Wrap 18" x 1500ft', category: 'packaging', unit: 'case', price: '$42.50' },
  { id: 'p2', name: 'Nitrile Gloves — Box of 100', category: 'safety', unit: 'box', price: '$11.90' },
  { id: 'p3', name: 'Packing Tape 48mm', category: 'packaging', unit: 'case', price: '$68.00' },
];

const COLUMNS = [
  { field: 'name', headerName: 'Product', flex: 2.6 },
  { field: 'category', headerName: 'Category', flex: 1 },
  { field: 'unit', headerName: 'Unit', flex: 0.7 },
  { field: 'price', headerName: 'Price', flex: 0.7 },
  {
    field: 'actions',
    headerName: '',
    width: 56,
    sortable: false,
    filterable: false,
    renderCell: () => (
      <IconButton size="small" aria-label="Actions">
        <MoreHorizIcon />
      </IconButton>
    ),
  },
];

const MOCK_ADMIN_ORDERS = [
  {
    orderNumber: '1042', username: 'Jordan M.', status: 'pending', totalPrice: 102,
    createdAt: '2026-08-18T10:00:00Z',
    message: 'Please leave at the loading dock, not the front office, thanks so much for your help today',
    orderItems: [{
      productName: 'Stretch Wrap 18" x 1500ft', quantity: 3, unitPrice: 42.5,
      originalPrice: 127.5, discountedPrice: 102, discount: 25.5, subtotal: 102,
      appliedCoupons: [{ couponId: 'c1', code: 'SUMMER20', color: '#7C3AED' }],
    }],
  },
  {
    orderNumber: '1041', username: 'Alex R.', status: 'accepted', totalPrice: 60,
    createdAt: '2026-08-17T10:00:00Z',
    orderItems: [{ productName: 'Nitrile Gloves — Box of 100', quantity: 5, unitPrice: 12, subtotal: 60 }],
  },
];

const Section = ({ title, children }) => (
  <Box sx={{ mb: 5 }}>
    <Typography variant="h5" sx={{ mb: 2 }}>{title}</Typography>
    {children}
    <Divider sx={{ mt: 3 }} />
  </Box>
);

// Scratch route (not linked from the nav) so every shared component's
// states are reachable without a live server — see COMPONENTS.md.
const DesignSystemPreview = () => {
  const { t, i18n } = useTranslation();
  const [tableStatus, setTableStatus] = useState('ready');
  const [category, setCategory] = useState('all');
  const [selection, setSelection] = useState(['p2']);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [pickerValue, setPickerValue] = useState('p2');
  const [qty, setQty] = useState(142);
  const [adminDetailOrder, setAdminDetailOrder] = useState(null);

  const emptyByKind = tableStatus === 'empty'
    ? { kind: 'none', icon: CategoryIcon, title: t('No products yet', { defaultValue: 'No products yet' }), description: t('Add your first product to start building the catalogue.', { defaultValue: 'Add your first product to start building the catalogue.' }), actions: [{ label: t('Add product', { defaultValue: 'Add product' }), variant: 'contained', onClick: () => {} }] }
    : tableStatus === 'no-match'
      ? { kind: 'no-match', title: t('No products match "hi-vis xl"', { defaultValue: 'No products match "hi-vis xl"' }), description: t('In Safety, price under $20.', { defaultValue: 'In Safety, price under $20.' }), actions: [{ label: t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => {} }, { label: t('Search all categories', { defaultValue: 'Search all categories' }), variant: 'outlined', onClick: () => {} }] }
      : undefined;

  const rows = tableStatus === 'empty' || tableStatus === 'no-match' ? [] : MOCK_ROWS;
  const gridStatus = ['empty', 'no-match'].includes(tableStatus) ? 'ready' : tableStatus;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Design system preview</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Scratch route — every shared component and every state, reachable without a live server.
      </Typography>

      <Section title="PageHeader">
        <PageHeader
          title={t('Products', { defaultValue: 'Products' })}
          subtitle={t('128 products across 6 categories', { defaultValue: '128 products across 6 categories' })}
          search={{ value: search, onChange: (e) => setSearch(e.target.value), placeholder: t('Search products', { defaultValue: 'Search products' }) }}
          actions={[
            { label: t('Export CSV', { defaultValue: 'Export CSV' }), icon: DownloadIcon, variant: 'outlined' },
            { label: t('Add product', { defaultValue: 'Add product' }), icon: AddIcon, variant: 'contained' },
            { label: t('Delete', { defaultValue: 'Delete' }), icon: DeleteIcon, variant: 'outlined', disabled: true, disabledReason: t('Select at least one row.', { defaultValue: 'Select at least one row.' }) },
          ]}
        />
      </Section>

      <Section title="MetricCard">
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Box sx={{ width: 220 }}>
            <MetricCard label={t('Revenue today', { defaultValue: 'Revenue today' })} value="$8,412" delta={{ text: '+12%', direction: 'up' }} sparkline={[38, 52, 44, 61, 55, 72, 66]} />
          </Box>
          <Box sx={{ width: 220 }}>
            <MetricCard label={t('Pending review', { defaultValue: 'Pending review' })} value="7" delta={{ text: 'needs review', direction: 'down' }} tone="warning" />
          </Box>
        </Stack>
      </Section>

      <Section title="StatusChip & CategoryPill">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <StatusChip status="pending" label={t('Pending', { defaultValue: 'Pending' })} />
          <StatusChip status="paid" label={t('Paid', { defaultValue: 'Paid' })} />
          <StatusChip status="overdue" label={t('Overdue', { defaultValue: 'Overdue' })} />
          <StatusChip status="draft" label={t('Draft', { defaultValue: 'Draft' })} />
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <CategoryPill category="packaging" label={t('Packaging', { defaultValue: 'Packaging' })} />
          <CategoryPill category="safety" label={t('Safety', { defaultValue: 'Safety' })} />
          <CategoryPill category="equipment" label={t('Equipment', { defaultValue: 'Equipment' })} />
        </Stack>
      </Section>

      <Section title="FilterChips + BulkBar">
        <Box sx={{ mb: 2 }}>
          <FilterChips
            options={[
              { value: 'all', label: t('All', { defaultValue: 'All' }) },
              { value: 'packaging', label: t('Packaging', { defaultValue: 'Packaging' }), categoryDot: 'packaging' },
              { value: 'safety', label: t('Safety', { defaultValue: 'Safety' }), categoryDot: 'safety' },
            ]}
            value={category}
            onChange={setCategory}
            filtersLabel={t('Filters', { defaultValue: 'Filters' })}
          />
        </Box>
        <BulkBar
          count={selection.length}
          countLabel={t('{{count}} selected', { count: selection.length, defaultValue: `${selection.length} selected` })}
          actions={[{ label: t('Export', { defaultValue: 'Export' }), onClick: () => {} }, { label: t('Delete', { defaultValue: 'Delete' }), onClick: () => {} }]}
          onClear={() => setSelection([])}
        />
      </Section>

      <Section title="AddToCartControl">
        <AddToCartControl available={1000} onAdd={() => {}} unit="sqf" />
      </Section>

      <Section title="ClientOrderRow (admin) + OrderDetailDialog">
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '20px', overflow: 'hidden' }}>
          {MOCK_ADMIN_ORDERS.map((order) => (
            <ClientOrderRow
              key={order.orderNumber}
              t={t}
              i18n={i18n}
              order={order}
              onView={setAdminDetailOrder}
              onAccept={() => {}}
              onReject={() => {}}
              onMarkPaid={() => {}}
              onMarkUnpaid={() => {}}
            />
          ))}
        </Box>
        <OrderDetailDialog
          order={adminDetailOrder}
          open={Boolean(adminDetailOrder)}
          onClose={() => setAdminDetailOrder(null)}
          t={t}
          i18n={i18n}
        />
      </Section>

      <Section title="FieldRow">
        <Box sx={{ maxWidth: 320 }}>
          <FieldRow label={t('Coupon code', { defaultValue: 'Coupon code' })} helper={t('This is what customers will enter at checkout', { defaultValue: 'This is what customers will enter at checkout' })}>
            <TextField fullWidth placeholder="SAVE20" />
          </FieldRow>
          <FieldRow label={t('Price', { defaultValue: 'Price' })} error={t('Price is required', { defaultValue: 'Price is required' })}>
            <TextField fullWidth error />
          </FieldRow>
        </Box>
      </Section>

      <Section title="StockLevel, QuantityStepper, RowThumb">
        <Stack spacing={2} sx={{ maxWidth: 360 }}>
          <StockLevel quantity={4} threshold={10} unit={t('units', { defaultValue: 'units' })} labels={{ inStock: t('In stock', { defaultValue: 'In stock' }), lowStock: t('Low stock', { defaultValue: 'Low stock' }), outOfStock: t('Out of stock', { defaultValue: 'Out of stock' }) }} />
          <StockLevel quantity={96} threshold={10} unit={t('units', { defaultValue: 'units' })} labels={{ inStock: t('In stock', { defaultValue: 'In stock' }), lowStock: t('Low stock', { defaultValue: 'Low stock' }), outOfStock: t('Out of stock', { defaultValue: 'Out of stock' }) }} />
          <QuantityStepper value={qty} unit={t('case', { defaultValue: 'case' })} min={0} onChange={setQty} previousValue={128} wasLabel={t('Was', { defaultValue: 'Was' })} />
          <RowThumb name="Nitrile Gloves" />
        </Stack>
      </Section>

      <Section title="SkeletonCards">
        <SkeletonCards rows={2} />
      </Section>

      <Section title="ProductPicker">
        <Box sx={{ maxWidth: 420 }}>
          <ProductPicker
            products={MOCK_PRODUCTS}
            value={pickerValue}
            onChange={setPickerValue}
            searchValue=""
            onSearchChange={() => {}}
            searchPlaceholder={t('Search products...', { defaultValue: 'Search products...' })}
            emptyLabel={t('No products found', { defaultValue: 'No products found' })}
          />
        </Box>
      </Section>

      <Section title="DataTableCard — all states">
        <ToggleButtonGroup
          exclusive
          value={tableStatus}
          onChange={(e, next) => next && setTableStatus(next)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="loading">Loading</ToggleButton>
          <ToggleButton value="empty">Empty · none</ToggleButton>
          <ToggleButton value="no-match">Empty · no-match</ToggleButton>
          <ToggleButton value="error">Error</ToggleButton>
          <ToggleButton value="permission">Permission</ToggleButton>
          <ToggleButton value="ready">Ready</ToggleButton>
        </ToggleButtonGroup>

        <DataTableCard
          status={gridStatus}
          rows={rows}
          columns={COLUMNS}
          checkboxSelection
          getRowId={(row) => row.id}
          rowSelectionModel={selection}
          onRowSelectionModelChange={setSelection}
          toolbar={(
            <FilterChips
              options={[{ value: 'all', label: t('All', { defaultValue: 'All' }) }, { value: 'packaging', label: t('Packaging', { defaultValue: 'Packaging' }), categoryDot: 'packaging' }]}
              value={category}
              onChange={setCategory}
              filtersLabel={t('Filters', { defaultValue: 'Filters' })}
            />
          )}
          bulkBar={{
            count: selection.length,
            countLabel: `${selection.length} selected`,
            actions: [{ label: t('Delete', { defaultValue: 'Delete' }), onClick: () => {} }],
            onClear: () => setSelection([]),
          }}
          empty={emptyByKind}
          error={{
            title: t("Couldn't load products", { defaultValue: "Couldn't load products" }),
            description: t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' }),
            request: 'GET /products · 503 · 14:22:06',
            retryLabel: t('Try again', { defaultValue: 'Try again' }),
            onRetry: () => setTableStatus('ready'),
          }}
          permission={{
            title: t("You don't have access to Products", { defaultValue: "You don't have access to Products" }),
            description: t('Products are limited to workspace admins. Ask Jordan M. to change your role if you need them.', { defaultValue: 'Products are limited to workspace admins. Ask Jordan M. to change your role if you need them.' }),
            action: { label: t('Back to dashboard', { defaultValue: 'Back to dashboard' }), onClick: () => setTableStatus('ready') },
          }}
        />
      </Section>

      <Section title="EntityDrawer + ConfirmDialog + WizardDrawer">
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={() => setDrawerOpen(true)}>Open EntityDrawer</Button>
          <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>Open ConfirmDialog</Button>
          <Button variant="outlined" onClick={() => setWizardOpen(true)}>Open WizardDrawer</Button>
        </Stack>

        <EntityDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={t('Edit product', { defaultValue: 'Edit product' })}
          subtitle={t('Nitrile Gloves — Box of 100', { defaultValue: 'Nitrile Gloves — Box of 100' })}
          closeLabel={t('Close', { defaultValue: 'Close' })}
          cancelLabel={t('Cancel', { defaultValue: 'Cancel' })}
          primaryLabel={t('Save changes', { defaultValue: 'Save changes' })}
          onPrimary={() => setDrawerOpen(false)}
        >
          <FieldRow label={t('Name', { defaultValue: 'Name' })}>
            <TextField fullWidth defaultValue="Nitrile Gloves — Box of 100" />
          </FieldRow>
          <FieldRow label={t('Price', { defaultValue: 'Price' })}>
            <TextField fullWidth defaultValue="11.90" />
          </FieldRow>
        </EntityDrawer>

        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          tone="danger"
          icon={DeleteIcon}
          title={t('Delete 2 products?', { defaultValue: 'Delete 2 products?' })}
          description={t('This permanently removes them from your catalog and all category filters.', { defaultValue: 'This permanently removes them from your catalog and all category filters.' })}
          records={['Nitrile Gloves — Box of 100', 'Packing Tape 48mm']}
          cancelLabel={t('Cancel', { defaultValue: 'Cancel' })}
          confirmLabel={t('Delete products', { defaultValue: 'Delete products' })}
          onConfirm={() => setConfirmOpen(false)}
        />

        <WizardDrawer
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          title={t('Create Coupon', { defaultValue: 'Create Coupon' })}
          steps={[{ label: t('Basics', { defaultValue: 'Basics' }) }, { label: t('Discount', { defaultValue: 'Discount' }) }, { label: t('Expiry', { defaultValue: 'Expiry' }) }]}
          activeStep={wizardStep}
          onStepClick={setWizardStep}
          closeLabel={t('Close', { defaultValue: 'Close' })}
          backLabel={t('Back', { defaultValue: 'Back' })}
          nextLabel={t('Next', { defaultValue: 'Next' })}
          finalLabel={t('Create Coupon', { defaultValue: 'Create Coupon' })}
          onBack={() => setWizardStep((s) => Math.max(0, s - 1))}
          onNext={() => setWizardStep((s) => Math.min(2, s + 1))}
          onFinish={() => setWizardOpen(false)}
          isDirty
          discardConfirm={{
            title: t('Discard changes?', { defaultValue: 'Discard changes?' }),
            description: t('Are you sure you want to discard your changes?', { defaultValue: 'Are you sure you want to discard your changes?' }),
            cancelLabel: t('Cancel', { defaultValue: 'Cancel' }),
            confirmLabel: t('Discard', { defaultValue: 'Discard' }),
          }}
        >
          <Typography variant="body1">Step content goes here.</Typography>
        </WizardDrawer>
      </Section>
    </Container>
  );
};

export default DesignSystemPreview;
